import type { ReactNode } from 'react'
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import { useCourseData } from 'modules/course/hooks/use-course-data'
import { useCourseMenu } from 'modules/course/hooks/use-course-menu'
import { useResetCourseQuiz } from 'modules/course/hooks/use-reset-course-quiz'
import { PrimaryButtonWithCustomTheme } from '../components/primary-button-with-custom-theme'
import { useCourseQuiz } from '../hooks/use-course-quiz'
import { useIsUserAdminOrOwnerOfCourse } from '../hooks/use-is-user-admin-or-owner-of-course'
import useStepRouter from '../hooks/use-step-router'
import { addPublicCustomClassNamePrefix } from '../utils/add-class-prefix'
import { CourseQuizStateContext } from './course-quiz-state/context'
import { CourseStatusEnum } from './course-quiz-state/types'

export const CourseQuizResetButton = (): ReactNode => {
  const { t } = useTranslation()

  const { step } = useStepRouter()
  const { setQuizState } = useContext(CourseQuizStateContext)
  const { courseData } = useCourseData()
  const { mutate: mutateCourseQuiz } = useCourseQuiz({ courseQuizId: step?.id })
  const { mutate: mutateCourseMenu } = useCourseMenu({ courseId: courseData?.id })
  const { resetCourseQuiz, isFetching } = useResetCourseQuiz()

  const isUserAdminOrOwnerOfCourse = useIsUserAdminOrOwnerOfCourse()

  const handleResetQuiz = async (): Promise<void> => {
    if (step?.id) {
      await resetCourseQuiz(step.id)
      await mutateCourseMenu(
        data =>
          data?.map(module => ({
            ...module,
            quiz:
              module.quiz && module.quiz.id ? { ...module.quiz, completed: false } : module.quiz,
          })),
        false,
      )
      await mutateCourseQuiz()
      setQuizState({ status: CourseStatusEnum.in_progress })
    }
  }

  return (
    (isUserAdminOrOwnerOfCourse && (
      <PrimaryButtonWithCustomTheme
        className={`shrink-0 font-bold ${addPublicCustomClassNamePrefix('course__button--reset-quiz-results')}`}
        isLoading={isFetching}
        onClick={handleResetQuiz}
      >
        {t('course.quiz.reset_results_button')}
      </PrimaryButtonWithCustomTheme>
    )) ||
    null
  )
}
